// src/components/market-areas/ExportKMLDialog.jsx
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';

// Convert Web Mercator coordinates to WGS84
const webMercatorToWGS84 = (x, y) => {
  const lng = (x * 180) / 20037508.34;
  const lat = (Math.atan(Math.exp((y * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
  return [lng, lat];
};

// Collect all geometries from a market area into a single MultiPolygon
const collectMarketAreaGeometries = (area) => {
  const allRings = [];

  // Helper to add rings from a geometry
  const addGeometryRings = (geometry) => {
    if (!geometry || !geometry.rings) return;

    // Convert all rings and add them as a polygon
    const convertedRings = geometry.rings.map(ring =>
      ring.map(coord => webMercatorToWGS84(coord[0], coord[1]))
    );

    // Group rings into polygon (outer ring + holes)
    // First ring is typically the outer boundary, rest are holes
    if (convertedRings.length > 0) {
      allRings.push(convertedRings);
    }
  };

  // Collect from radius points
  if (area.ma_type === 'radius' && Array.isArray(area.radius_points)) {
    area.radius_points.forEach(pt => {
      if (pt.geometry) addGeometryRings(pt.geometry);
    });
  }

  // Collect from drive time points
  if (area.ma_type === 'drivetime' && Array.isArray(area.drive_time_points)) {
    area.drive_time_points.forEach(pt => {
      if (pt.geometry) addGeometryRings(pt.geometry);
    });
  }

  // Collect from locations
  if (area.locations && Array.isArray(area.locations)) {
    area.locations.forEach(loc => {
      if (loc.geometry) addGeometryRings(loc.geometry);
    });
  }

  // Collect from direct geometry on market area
  if (area.geometry && area.geometry.rings) {
    addGeometryRings(area.geometry);
  }

  // Collect from site_location_data
  if (area.site_location_data && Array.isArray(area.site_location_data)) {
    area.site_location_data.forEach(loc => {
      if (loc.geometry) addGeometryRings(loc.geometry);
    });
  }

  return allRings;
};

// Convert polygon coordinates to KML coordinate string (lng,lat,0 format)
const toKMLCoordinates = (ring) => {
  return ring.map(coord => `${coord[0]},${coord[1]},0`).join(' ');
};

// Create KML document for a market area (single placemark with MultiGeometry)
const createKML = (area, polygons) => {
  // Create polygon elements for each polygon
  const polygonElements = polygons.map((rings) => {
    // First ring is outer boundary, rest are inner boundaries (holes)
    const outerBoundary = `<outerBoundaryIs><LinearRing><coordinates>${toKMLCoordinates(rings[0])}</coordinates></LinearRing></outerBoundaryIs>`;
    const innerBoundaries = rings.slice(1).map(hole =>
      `<innerBoundaryIs><LinearRing><coordinates>${toKMLCoordinates(hole)}</coordinates></LinearRing></innerBoundaryIs>`
    ).join('');

    return `<Polygon>${outerBoundary}${innerBoundaries}</Polygon>`;
  }).join('\n        ');

  // Use MultiGeometry if multiple polygons, single Polygon otherwise
  const geometry = polygons.length > 1
    ? `<MultiGeometry>
        ${polygonElements}
      </MultiGeometry>`
    : polygonElements;

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${area.name || 'Market Area'}</name>
    <description>Market Area: ${area.ma_type || 'Unknown type'}</description>
    <Placemark>
      <name>${area.name || 'Unnamed'}</name>
      <description>${area.description || ''}</description>
      ${geometry}
    </Placemark>
  </Document>
</kml>`;
};

export default function ExportKMLDialog({ isOpen, onClose, marketAreas }) {
  const [folderName, setFolderName] = useState('market_areas_export');
  const [selectedMAIds, setSelectedMAIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    // Initially select all
    setSelectedMAIds(marketAreas.map(ma => ma.id));
  }, [marketAreas]);

  const handleToggleMA = (maId) => {
    setSelectedMAIds((prev) =>
      prev.includes(maId) ? prev.filter((id) => id !== maId) : [...prev, maId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMAIds(marketAreas.map(ma => ma.id));
    } else {
      setSelectedMAIds([]);
    }
  };

  const handleExportClick = async () => {
    if (!selectedMAIds || selectedMAIds.length === 0) {
      toast.error('No market areas selected');
      return;
    }

    setIsExporting(true);
    const loadingToast = toast.loading('Exporting KML files...');

    try {
      const zip = new JSZip();

      // Process each selected market area
      let processedCount = 0;
      for (const maId of selectedMAIds) {
        const area = marketAreas.find(m => m.id === maId);
        if (!area) continue;

        console.log('Processing market area:', area.name, area);

        // Collect all polygon geometries for this market area
        const polygons = collectMarketAreaGeometries(area);
        if (polygons.length === 0) {
          console.warn(`No geometry found for market area: ${area.name}`, {
            ma_type: area.ma_type,
            radius_points: area.radius_points,
            drive_time_points: area.drive_time_points,
            locations: area.locations,
            geometry: area.geometry,
            site_location_data: area.site_location_data
          });
          continue;
        }

        console.log('Created polygons:', polygons.length);
        processedCount++;

        const safeFileName = area.name.replace(/[^a-zA-Z0-9_-]/g, '_');

        // Create KML content
        const kmlContent = createKML(area, polygons);

        // Add KML file directly to the zip (no folder needed for single file per MA)
        zip.file(`${safeFileName}.kml`, kmlContent);

        console.log(`Added KML for ${area.name} with ${polygons.length} polygons`);
      }

      console.log(`Processed ${processedCount} market areas`);

      if (processedCount === 0) {
        toast.dismiss(loadingToast);
        toast.error('No valid geometries found to export');
        return;
      }

      // Generate the final ZIP file
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // Save the ZIP file
      saveAs(content, `${folderName}.zip`);

      toast.dismiss(loadingToast);
      toast.success(`Exported ${processedCount} KML files successfully`);
      onClose();
    } catch (error) {
      console.error('KML export failed:', error);
      toast.dismiss(loadingToast);
      toast.error(`Failed to export: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Transition.Child
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          as={Fragment}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6">
            <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Export MAs as KML
            </Dialog.Title>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Each market area will be exported as a single KML file (compatible with Google Earth, ArcGIS, and other GIS tools).
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Market Areas
                </label>
                <div className="mb-2">
                  <input
                    type="checkbox"
                    checked={selectedMAIds.length === marketAreas.length && marketAreas.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Select All</span>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2 border p-2 rounded-md dark:border-gray-600">
                  {marketAreas.map((ma) => (
                    <div key={ma.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedMAIds.includes(ma.id)}
                        onChange={() => handleToggleMA(ma.id)}
                        className="h-4 w-4 text-green-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {ma.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700
                  border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleExportClick}
                disabled={isExporting || selectedMAIds.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
